import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SelectButton } from 'primereact/selectbutton';
import { NewBornFilterDTO, NewBornGranularity, NewBornTimelinePointDTO } from '../../dto/newborn/NewBornDTO';
import { getNewBornTimeline } from '../../services/NewBornController';
import useSectionData from '../UrgencySummary/useSectionData';
import NewBornEvolutionChart from './NewBornEvolutionChart';
import NewBornPeakChips from './NewBornPeakChips';
import NewBornPeriodSidebar from './NewBornPeriodSidebar';
import {
    autoGranularity,
    cesareanPeaks,
    evolutionAverage,
    granularityOf,
    NEWBORN_MEASURES,
    NewBornMeasure,
    rangeReference,
} from './newBornEvolutionModel';
import { filtersKey } from './newBornFilterModel';

const GRANULARITIES: NewBornGranularity[] = ['WEEK', 'MONTH'];

const MEASURE_LABEL_ID: Record<NewBornMeasure, string> = {
    births: 'NewBornKpiBirths',
    cesarean: 'NewBornMeasureCesarean',
};

const GRANULARITY_LABEL_ID: Record<NewBornGranularity, string> = {
    WEEK: 'NewBornGranularityWeek',
    MONTH: 'NewBornGranularityMonth',
};

interface NewBornEvolutionProps {
    /** Período y filtros aplicados de la pantalla. */
    filters: NewBornFilterDTO;
    /** La medida y la agrupación elegidas viven en la pantalla: no se pierden al cambiar de pestaña ni de filtros. */
    measure: NewBornMeasure;
    onMeasureChange: (measure: NewBornMeasure) => void;
    /** Agrupación elegida; `null` es la que decide el backend según el largo del rango. */
    granularityChoice: NewBornGranularity | null;
    onGranularityChange: (granularity: NewBornGranularity) => void;
    /** Mínimo de casos para escribir «mejor» o «peor» (el de los indicadores de la pantalla). */
    minCases: number;
    /** «Ver solo este período» del panel: aplica sus fechas como período de la pantalla. */
    onViewPeriod: (from: string, to: string) => void;
}

/**
 * Pestaña «Evolución»: los nacimientos o el % de cesáreas por semana o mes, con la línea de promedio del rango, los picos del
 * % (chips «Picos» junto al gráfico) y, al tocar un período, el panel lateral con su detalle. «Semana | Mes» y «Nacimientos
 * | % cesáreas» son el mismo dato en otra vista (SelectButton): no llevan rótulo visible, solo nombre accesible. La consulta
 * (`/timeline`) se pide al abrir la pestaña y de nuevo con cada cambio de filtros o de agrupación, con su propia carga y su
 * propio error con «Reintentar»; los resultados quedan en un caché por período, filtros y agrupación.
 */
export default function NewBornEvolution({
    filters, measure, onMeasureChange, granularityChoice, onGranularityChange, minCases, onViewPeriod,
}: NewBornEvolutionProps) {
    const intl = useIntl();
    const timeline = useSectionData(
        `${filtersKey(filters)}|timeline|${granularityChoice ?? 'auto'}`,
        (signal) => getNewBornTimeline(filters, granularityChoice, signal),
    );
    const points = useMemo(() => timeline.data ?? timeline.stale ?? [], [timeline.data, timeline.stale]);
    // Lo que se muestra es lo que llegó: sus claves dicen si es por semana o por mes
    const granularity: NewBornGranularity = granularityOf(points) ?? granularityChoice ?? autoGranularity(filters.from, filters.to);
    const selectedGranularity: NewBornGranularity = granularityChoice
        ?? (timeline.data ? granularityOf(timeline.data) : null) ?? autoGranularity(filters.from, filters.to);
    const refreshing = timeline.status === 'loading';
    const hasBirths = points.some((point) => point.births > 0);

    const analysis = useMemo(() => (measure === 'cesarean' ? cesareanPeaks(points) : null), [points, measure]);
    const average = useMemo(() => evolutionAverage(points, measure, granularity), [points, measure, granularity]);
    const reference = useMemo(() => rangeReference(points, granularity), [points, granularity]);

    // ── Panel del período ──
    const [detail, setDetail] = useState<NewBornTimelinePointDTO | null>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    /** Abre el panel de un período; al cerrarlo el foco vuelve a `opener` (el gráfico o el chip). */
    const openPeriod = useCallback((point: NewBornTimelinePointDTO, opener: HTMLElement | null) => {
        openerRef.current = opener;
        setDetail(point);
    }, []);
    const closeDetail = useCallback(() => {
        setDetail(null);
        const opener = openerRef.current;
        openerRef.current = null;
        // Cuando el panel ya se cerró; si el gráfico o el chip ya no están, no hay a dónde volver
        if (opener) window.setTimeout(() => { if (opener.isConnected) opener.focus(); }, 0);
    }, []);
    const viewPeriod = useCallback((from: string, to: string) => {
        onViewPeriod(from, to);
        closeDetail();
    }, [onViewPeriod, closeDetail]);

    const measureOptions = NEWBORN_MEASURES.map((value) => ({ value, label: intl.formatMessage({ id: MEASURE_LABEL_ID[value] }) }));
    const granularityOptions = GRANULARITIES.map((value) => ({ value, label: intl.formatMessage({ id: GRANULARITY_LABEL_ID[value] }) }));
    const chartLabel = `${intl.formatMessage({ id: MEASURE_LABEL_ID[measure] })} · ${intl.formatMessage({ id: GRANULARITY_LABEL_ID[granularity] })}`;

    return (
        <section className="nv-newborn-evolution" aria-label={intl.formatMessage({ id: 'NewBornTabEvolution' })}>
            <div className="nv-newborn-evolution__controls">
                <SelectButton
                    value={selectedGranularity}
                    options={granularityOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => { if (event.value) onGranularityChange(event.value as NewBornGranularity); }}
                    aria-label={intl.formatMessage({ id: 'NewBornGranularityBy' })}
                />
                <SelectButton
                    value={measure}
                    options={measureOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => { if (event.value) onMeasureChange(event.value as NewBornMeasure); }}
                    aria-label={intl.formatMessage({ id: 'NewBornMeasureBy' })}
                />
            </div>

            <div className="nv-newborn-evolution__body" aria-busy={refreshing}>
                {refreshing && (
                    <div className="nv-newborn-loading" role="status">
                        <div className="nv-loading nv-newborn-loading__box">
                            <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
                            <span>{intl.formatMessage({ id: 'NewBornLoading' })}</span>
                        </div>
                    </div>
                )}

                {timeline.status === 'failed' && (
                    <div className="nv-card nv-newborn-error" role="alert">
                        <p className="nv-note">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            {intl.formatMessage({ id: 'NewBornEvolutionError' })}
                        </p>
                        <Button type="button" className="buttonMain2" outlined label={intl.formatMessage({ id: 'NewBornRetry' })} onClick={timeline.retry} />
                    </div>
                )}

                {timeline.status !== 'failed' && points.length > 0 && !hasBirths && !refreshing && (
                    <p className="nv-muted">{intl.formatMessage({ id: 'NewBornEvolutionEmpty' })}</p>
                )}

                {points.length > 0 && hasBirths && (
                    <div className={`nv-newborn-evolution__content${refreshing ? ' nv-newborn-blurred' : ''}`}>
                        <NewBornEvolutionChart
                            points={points}
                            granularity={granularity}
                            measure={measure}
                            ariaLabel={chartLabel}
                            average={average}
                            analysis={analysis}
                            onOpen={openPeriod}
                        />
                        {analysis && <NewBornPeakChips points={points} granularity={granularity} analysis={analysis} onOpen={openPeriod} />}
                    </div>
                )}
            </div>

            <NewBornPeriodSidebar
                point={detail}
                granularity={granularity}
                filters={filters}
                reference={reference}
                minCases={minCases}
                onViewPeriod={viewPeriod}
                onHide={closeDetail}
            />
        </section>
    );
}
